import type {
  ColumnWidth,
  DataTableColumnRef,
  DataTableColumnRefInput,
  DataTableColumnWidth,
  DataTableSortDescriptor,
  DataTableSortDescriptorInput,
} from '../types';

const SEPARATOR = ':';
const PREFIXES = { standard: 's', attribute: 'a' } as const;

export function toColumnRef(input: DataTableColumnRefInput): DataTableColumnRef {
  return typeof input === 'string' ? { type: 'standard', id: input } : input;
}

export function encodeColumnRef(ref: DataTableColumnRef) {
  return `${PREFIXES[ref.type]}${SEPARATOR}${ref.id}`;
}

export function encodeColumnInput(input: DataTableColumnRefInput) {
  return encodeColumnRef(toColumnRef(input));
}

export function standardColumnKey(id: string) {
  return encodeColumnRef({ type: 'standard', id });
}

export function attributeColumnKey(key: string) {
  return encodeColumnRef({ type: 'attribute', id: key });
}

export function decodeColumnKey(key: unknown): DataTableColumnRef | undefined {
  if (typeof key !== 'string') {
    return undefined;
  }
  const index = key.indexOf(SEPARATOR);
  if (index === -1) {
    return undefined;
  }
  const prefix = key.slice(0, index);
  const id = key.slice(index + SEPARATOR.length);
  if (prefix === PREFIXES.standard) {
    return { type: 'standard', id };
  }
  if (prefix === PREFIXES.attribute) {
    return { type: 'attribute', id };
  }
  return undefined;
}

export function decodeColumnKeys(keys: readonly string[]) {
  return keys.flatMap((key) => {
    const ref = decodeColumnKey(key);
    return ref ? [ref] : [];
  });
}

export function encodeSortDescriptor(descriptor: DataTableSortDescriptorInput | undefined) {
  return descriptor ? { column: encodeColumnInput(descriptor.column), direction: descriptor.direction } : undefined;
}

export function decodeSortDescriptor(
  descriptor: { column: string; direction: DataTableSortDescriptor['direction'] } | undefined,
): DataTableSortDescriptor | undefined {
  const column = descriptor ? decodeColumnKey(descriptor.column) : undefined;
  return descriptor && column ? { column, direction: descriptor.direction } : undefined;
}

export function decodeColumnWidths(widths: Map<unknown, ColumnWidth>): DataTableColumnWidth[] {
  return [...widths].flatMap(([key, width]) => {
    const column = decodeColumnKey(key);
    return column ? [{ column, width }] : [];
  });
}
